import { types as t, flow, Instance } from '@invisr/mobx-state-tree'
import crudActions from './crud-actions'
import { observable, action, makeObservable } from 'mobx'
import Mapping from './mapping'
import OAuth2 from '../../utils/oauth2'
import errorToast from '../../utils/error-toast'
import secretsActions from './secrets-actions'

const OAuth2ResultOptionalFieldsDefaultValues = {
  ext_expires_in: 0,
  refresh_token: '',
  scope: '' ,
}

const OAuth2ResultRequiredFieldsDefaultValues = {
  token: '',
  access_token: '',
  token_type: '',
  expires_in: 0,
  _exp: 0,
}

const OAuth2ResultFieldsDefaultValues = Object.assign(OAuth2ResultRequiredFieldsDefaultValues, OAuth2ResultOptionalFieldsDefaultValues)

type OAuth2Result = typeof OAuth2ResultRequiredFieldsDefaultValues & Partial<typeof OAuth2ResultOptionalFieldsDefaultValues>

export class CredentialsProvider {
  data: OAuth2Result = null
  config
  auth = null

  constructor(config) {
    makeObservable(this, {
      data: observable,
      query: action,
      setData: action
    })

    this.config = config
  }

  setData(data: OAuth2Result) {
    this.data = data
  }

  query = async () => {
    switch (this.config?.credentialsProvider) {
      case 'OAuth2-PKCE':
        const opts = {
          ...this.config.mapping.children
          .filter(c => c.type === 'string' && c.bindingType === 'value')
          .reduce((o, c) => ({ ...o, [c.key]: c.value || '' }), {}),
          ...this.config.mapping.toProps
        }

        if (!this.auth) {
          this.auth = new OAuth2(this.config.id)
          this.auth.on('change', (token) => this.setData(token))
        }
        try {
          this.setData(await this.auth.getToken({ ...this.config.metadata || {}, ...opts || {} }))
        } catch (e) {
          errorToast(`${this.config.name} getToken`, e?.message)
        }

        return this.data

      default:
        return []
    }
    return []
  }
}

export const defaultCredentialsMapping = {
  type: 'object',
  children: [{ key: 'key', type: 'string' }]
}

const CredentialsDataSource = t
  .model('Credentials', {
    type: t.optional(t.string, 'Credentials'),
    credentialsProvider: t.optional(t.string, ''),
    mapping: t.late(() => t.optional(Mapping, defaultCredentialsMapping)),
    metadata: t.frozen()
  })
  .views(self => ({
    get request() {
      return self.mapping.toProps || {}
    },
    get provider() {
      return CredentialsProvider
    },
    getFields() {
      return ['name', 'provider', 'createdDate'].map(x => ({
        createable: false,
        editable: false,
        fieldPath: x,
        isNameField: false,
        label: x,
        readable: true,
        referenceTo: '',
        sobjectAPIName: 'Credentials',
        type: 'STRING'
      }))
    }
  }))
  .views(self => ({
    labels() {
      return {
        key: (self as any).id,
        label: (self as any).name,
        childNodes: [
          {
            key: 'data',
            label: 'Data',
            isExpanded: false,
            childNodes: self.credentialsProvider === 'OAuth2-PKCE' ? Object.entries(OAuth2ResultFieldsDefaultValues).map(([elem, val]) => ({
              key: elem,
              label: elem,
              fieldType: typeof val,
              isExpanded: false
            })) : []
          }
        ]
      }
    }
  }))
  .actions(crudActions)
  .actions(secretsActions)
  .actions(self => ({
    async updateOAuthMetadata() {
      const { authority } = self.mapping.toProps
      if (authority) {
        const r = await fetch(authority + '/.well-known/openid-configuration', { mode: 'cors' })
        const { token_endpoint, authorization_endpoint, userinfo_endpoint, end_session_endpoint } = await r.json()
        self.update('metadata', { token_endpoint, authorization_endpoint, userinfo_endpoint, end_session_endpoint })
      }
    },
    onClone: flow(function* (newDbKey: string) {
      const secretFields = self.getSecretFields(self.mapping.children)?.filter(f => f.value);
      const data = {
        provider: 'Credentials',
        sourceCredentialsDbKey: (self as any).dbKey,
        targetCredentialsDbKey: newDbKey,
        secrets: {
          fields: secretFields
        }
      };
      yield self.cloneSecretData(data);
    }),
    afterSave: flow(function* (_, after: Instance<typeof CredentialsDataSource>) {
      const secretFields = after.getSecretFields(after.mapping.children)?.filter(f => f.value);
      after.clearSecretFields(after.mapping.children);

      const data = {
        provider: 'Credentials',
        dbKey: (after as any).dbKey,
        secrets: {
          fields: secretFields
        }
      };
      yield self.saveSecretData(data);
    })
  }))

export type ICredentialsDataSource = Instance<typeof CredentialsDataSource>;
export default CredentialsDataSource;
